import { useRef, useState } from 'react';
import { Modal, ModalBody, Box, Text, Heading, Button } from '@razorpay/blade/components';
import { OrgLogoImage } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/components/OrgLogoImage';
import NavigationLink from 'components/ui/NavigationLink/NavigationLink';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import html2canvas from 'html2canvas';
import { OnboardingCompletedProps } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import onboardingCompletedImage from 'assets/employeeOnboarding/onboardingCompleted.png';

export const OnboardingCompleted = ({
  isOpen = true,
  onDismiss = () => {},
  designation,
}: OnboardingCompletedProps) => {
  const socialMediaContentRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const organizationBrandName =
    useSelector(loggedInUserSelectors.organizationBrandName) || 'Company';

  const handleDownload = async () => {
    if (!socialMediaContentRef.current) return;

    try {
      setIsDownloading(true);
      const pixelRatio = window.devicePixelRatio || 1;

      const canvas = await html2canvas(socialMediaContentRef.current, {
        useCORS: true,
        allowTaint: false,
        scale: pixelRatio * 2,
        foreignObjectRendering: false,
      });

      const link = document.createElement('a');
      link.download = 'social-media-post.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading image:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="medium"
      accessibilityLabel="Social Media Post">
      <ModalBody padding="spacing.0">
        <Box
          padding="spacing.8"
          ref={socialMediaContentRef}
          position="relative"
          minHeight="216px"
          overflow="hidden">
          <Box position="absolute" top="spacing.0" left="spacing.0" width="100%" height="100%">
            <img
              src={`${onboardingCompletedImage}?time=${new Date().valueOf()}`}
              alt="Onboarding Completed"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
              crossOrigin=""
            />
          </Box>
          <Box
            position="relative"
            zIndex="1"
            display="flex"
            flexDirection="column"
            width="70%"
            flex={1}
            justifyContent="center">
            <Box>
              <OrgLogoImage />
            </Box>
            <Heading size="large" marginTop="spacing.4" marginBottom="spacing.4">
              I'm excited to join {organizationBrandName}!
            </Heading>
            <Text size="medium" color="surface.text.staticWhite.muted" weight="regular">
              I'm thrilled to share that I've joined {organizationBrandName}
              {designation ? ` as a ${designation}` : ''}!
            </Text>
          </Box>
        </Box>

        <Box backgroundColor="surface.background.gray.intense" padding="spacing.8" textAlign="left">
          <Heading size="medium" color="surface.text.staticWhite.normal" marginBottom="spacing.4">
            Welcome to {organizationBrandName}!
          </Heading>
          <Text size="medium" color="surface.text.staticWhite.muted" marginBottom="spacing.7">
            We're excited to welcome you to the team and thrilled to have you on board! Download the
            image and share your new job update with your network!
          </Text>

          <Box display="flex" justifyContent="right" gap="spacing.4">
            {/* 
            TODO: uncomment this once cors issue is resolved
            <Button
              variant="secondary"
              size="medium"
              isLoading={isDownloading}
              onClick={handleDownload}>
              Download
            </Button> */}
            <NavigationLink href="/dashboard">
              <Button variant="primary" size="medium">
                Go to Dashboard
              </Button>
            </NavigationLink>
          </Box>
        </Box>
      </ModalBody>
    </Modal>
  );
};
